// Fix for Room.tsx to ensure proper game state updates

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GameContainer } from '@components/game/GameContainer';
import { WaitingRoom } from '@components/game/WaitingRoom';
import { DebugSocket } from '@components/debug/DebugSocket';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { useGameStore, useRoomStore } from '@store/index';
import { useSocket } from '@hooks/useSocket';
import { useRoomSync } from '@hooks/useRoomSync';
import { socketService } from '@services/SocketService';
import styles from './Room.module.css';

export const Room: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const gameState = useGameStore((state) => state.gameState);
  const roomCode = useRoomStore((state) => state.roomCode);
  const opponent = useRoomStore((state) => state.opponent);
  const playerColor = useRoomStore((state) => state.playerColor);
  const [isLoading, setIsLoading] = useState(true);
  const [, forceUpdate] = useState({});
  const renderCount = useRef(0);
  
  // Important: These hooks set up the socket event listeners
  useSocket();
  useRoomSync();
  
  // Subscribe to game store changes explicitly
  useEffect(() => {
    const unsubscribe = useGameStore.subscribe((state) => {
      const gameState = state.gameState;
        console.log('[Room] Game state changed in store:', {
          status: gameState?.gameStatus,
          hasBoard: !!gameState?.board,
          renderCount: ++renderCount.current
        });
        // Force re-render
        forceUpdate({});
      }
    );
    
    return unsubscribe;
  }, []);
  
  useEffect(() => {
    // Redirect if no room code or mismatch
    if (!roomCode || roomCode !== code?.toUpperCase()) {
      console.log('Redirecting - no room code or mismatch', { roomCode, code });
      navigate('/');
      return;
    }
    
    // Give socket time to connect and register events
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [roomCode, code, navigate]);
  
  useEffect(() => {
    console.log('[Room] Component rendered:', { 
      gameState: gameState?.gameStatus,
      opponent: opponent?.name,
      playerColor,
      roomCode,
      isConnected: socketService.isConnected(),
      renderCount: renderCount.current
    });
  }, [gameState, opponent, playerColor, roomCode]);
  
  // Show loading briefly to ensure socket is ready
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Connecting...</div>
      </div>
    );
  }
  
  // Check game state more explicitly
  const isGameActive = gameState && 
                      gameState.gameStatus === 'active' && 
                      gameState.board && 
                      gameState.board.squares;
  
  // Show game if it has started
  if (isGameActive) {
    console.log('[Room] Showing game view');
    return (
      <ErrorBoundary>
        <GameContainer />
      </ErrorBoundary>
    );
  }
  
  // Show waiting room
  console.log('[Room] Showing waiting room');
  return (
    <div className={styles.container}>
      <DebugSocket />
      <WaitingRoom 
        roomCode={roomCode || ''}
        playerColor={playerColor || ''}
        opponent={opponent}
      />
    </div>
  );
};